import { classNames } from "@NBCUDTC/reactjs-utility-belt/classnames";

import styles from "./Text.module.scss";
import { TextProps, TextVariant } from "./types";

/**
 * Renders text with customizable variants and styles.
 *
 * @param children - The content to be displayed within the text component.
 * @param variant - The HTML element type to render the text as (e.g., "p", "span", "h1"). Defaults to `"p"`.
 * @param truncate - Determines whether the text should be truncated with ellipsis if it overflows. Defaults to `false`.
 * @param className - Additional CSS class names to apply to the component.
 * @param attr - Additional HTML attributes to pass to the rendered element.
 *
 * @returns The rendered text component as a React element.
 *
 * @example
 * ```tsx
 * <Text variant="h1" truncate className="custom-class">
 *   Hello World!
 * </Text>
 * ```
 */
const Text = ({
  children,
  variant = "p",
  truncate = false,
  className,
  ...attr
}: TextProps) => {
  const Component = variant as TextVariant;

  const cn = classNames(
    styles["text"],
    truncate && styles["truncate"],
    className,
  );

  return (
    <Component className={cn} {...attr}>
      {children}
    </Component>
  );
};

export { Text };
